import React, { memo, useCallback, useEffect, useMemo } from 'react';
import parse from 'html-react-parser';
import { equals } from 'ramda';
import { log } from 'log';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { componentService } from 'js/services/component.service';
import { useSmartClosePopups } from 'js/components/common/PopupManager';
import { getHTMLParserOptions } from 'js/utils/getHTMLParserOptions.util';
import { getBackboneReplacerViews } from 'js/backbone/components/BackboneReplacer';

import { dispatcher } from 'dispatcher';

/**
 * @param props {Object}
 * @param [props.id] {string}
 * @param [props.content] {string}
 * @param [props.context] {string}
 * @param [props.popupId] {string}
 * @param [props.className] {string}
 */
export const WebContentInnerBare = ({ id, content, context, popupId, className = '' }) => {
    const isMounted = useIsMounted();
    const replacers = getBackboneReplacerViews();
    const components = componentService.getWebContentComponents();
    const closePopups = useSmartClosePopups();

    const { options, scripts } = useMemo(() => getHTMLParserOptions({
        context,
        components,
        replacers
    }), [components, replacers, context]);

    const onLinkClick = useCallback((event) => {
        if (event.target.tagName !== 'A') {
            return;
        }

        const { target: { target, href } } = event;
        const isInTheSameWindow = target === '_self';
        const isHashLink = href === '#';

        if (popupId && isInTheSameWindow && !isHashLink) {
            closePopups(popupId);
        }
    }, [popupId, closePopups]);

    useEffect(() => {
        if (isMounted()) {
            for (const script of scripts) {
                try {
                    document.body.appendChild(script);
                } catch (e) {
                    log('error', `Error in webcontent ${id} script`, e);
                }
            }

            if (id) {
                dispatcher.dispatch('webContent:rendered', id);
            }

            return () => {
                for (const script of scripts) {
                    try {
                        document.body.removeChild(script);
                    } catch (e) {
                        log('error', `Unable to remove script from webcontent ${id}`, e);
                    }
                }
            };
        }
    }, [id, isMounted, scripts]);

    const regexp = /<body[^>]*>(.*)<\/body>/gis;
    const bodyEntries = regexp.exec(content);
    const contentToParse = bodyEntries ? bodyEntries[1] : content;
    const parsedContent = parse(contentToParse.toString(), options);

    return (
        <div
            onClick={onLinkClick}
            className={`${className}`}
            {...id ? { 'data-web-content-id': id } : {}}
        >
            <article>{parsedContent}</article>
        </div>
    );
};

export const WebContentInner = memo(WebContentInnerBare, equals);
